// CResultMoneyScene — 牌局結束畫面 3／3：籌碼結算
// 對應反編譯原版 GameResultMoneyScene / PlayerResultMoneyEntity。
//
// 內容：金框面板 + 4 列玩家資訊；每列先顯示「原金額 ± 本局輸贏」，
// 等待 2.5 秒後總額跑向結算值、輸贏數字同步歸零並播放原 APP 籌碼聲。
// 只有按下按鈕才會立即完成跑分；鍵盤／遙控器可左右選擇與確認。
//
// 正式對局顯示永久帳本原子結算後的 before／delta／after；獨立預覽才使用 mock 籌碼。
//
// === 使用圖片 ===
// - 面板與標題使用 page-skin 程式繪製；舊 result-dialog/title/npc/bubble/tip 預留但目前未採用。
// - 桌背景 + 遮罩 + header 圖 + 離開/繼續鈕：見 CBaseResultScene 檔頭
// - public/assets/ui/result-win.png / result-lose.png / result-draw.png — 勝／敗／和章
// - public/assets/ui/number-4.png（key: NUMBER_4_KEY）— 金色籌碼數字
// - public/assets/ui/result-number-symbol.png（key: RESULT_NUMBER_SYMBOL_KEY）— ＋／－
// - public/assets/ui/result-btn-exit.png / result-btn-continue.png — 離開／繼續

// 跨局彈出面板與按鈕透過 dialog-skin 使用木紋金框，素材詳見該檔檔頭。
import {
  type CDialogButtonSurface,
  createDialogPanel,
  createDialogButtonSurface,
} from '@ui/dialog-skin';
import Phaser from 'phaser';
import { glow, showDialog, reducedMotion, watchReducedMotion } from '@ui/motion';
import { createPageTitle, PAGE_THEME } from '@ui/page-skin';
import { playUIClick } from '@audio/scene-audio';

import { LOGICAL_HEIGHT, LOGICAL_WIDTH } from '@config/game-config';
import {
  GameSetCompletionReason,
  GameSetStatus,
  canAllPlayersAfford,
  completeGameSetForInsufficientBalance,
  prepareNextRound,
  rollDice,
  type IGameSetSession,
} from '@core/index';
import { traceGameSet } from '../debug/round-trace';
import { RoundSummaryKind } from '@core/summary';
import type { TSeat } from '@core/types';
import { loadGameSave, loadSettings, saveActiveSession } from '@storage/index';

import { CBaseResultScene } from './base-result-scene';
import {
  NUMBER_4_FRAME_COMMA,
  NUMBER_4_FRAME_WIDTH,
  NUMBER_4_KEY,
  RESULT_BTN_CONTINUE_KEY,
  RESULT_BTN_EXIT_KEY,
  RESULT_DRAW_KEY,
  RESULT_LOSE_KEY,
  RESULT_NUMBER_SYMBOL_FRAME_WIDTH,
  RESULT_NUMBER_SYMBOL_KEY,
  RESULT_WIN_KEY,
  SFX_RESULT_MONEY_KEY,
} from './boot-scene';
import { ChipOutcome } from './result-flow-data';
import type { TResultChip, TResultFlowData } from './result-flow-data';
import { buildResultMoneyGlyphLayout, formatResultChip } from './result-money-layout';
import { getMoneyRunDurationMs, MONEY_RUN_DELAY_MS } from './result-money-timing';
import { nextRound } from '@net/account-client';
import { isServerTableEnabled } from '@net/server-mode';
import { noticeFromError } from '@ui/notice-toast';
import { SceneKey } from './scene-keys';

// ============================================================================
// 版面常數
// ============================================================================

// ---- 對話面板（nineslice 金框）----
const PANEL_CX = 640; // 面板置中
const PANEL_CY = 326;
const PANEL_W = 1100;
const PANEL_H = 560;
const PANEL_LEFT = PANEL_CX - PANEL_W / 2;
const PANEL_TOP = PANEL_CY - PANEL_H / 2;

// ---- 結算標題 ----
const TITLE_Y = PANEL_TOP + 44;

// ---- 每列綠色子框 ----
const ROW_PANEL_LEFT = PANEL_LEFT + 92;
const ROW_PANEL_RIGHT = PANEL_LEFT + PANEL_W - 38;
const ROW_PANEL_W = ROW_PANEL_RIGHT - ROW_PANEL_LEFT;
const ROW_PANEL_TOP_OFFSET = -6;
const ROW_PANEL_HEIGHT = 104;
const ROW_PANEL_RADIUS = 18;
const ROW_PANEL_FILL_TOP = PAGE_THEME.row;
const ROW_PANEL_FILL_BOTTOM = PAGE_THEME.row;
const ROW_PANEL_BORDER = PAGE_THEME.gold;
const ROW_PANEL_BORDER_WIDTH = 2;

// ---- 4 列玩家籌碼 ----
const ROW_FIRST_TOP = PANEL_TOP + 96;
const ROW_STEP = 112;
const BADGE_CX = PANEL_LEFT + 96;
const BADGE_SCALE = 0.72;
const HEADER_X = PANEL_LEFT + 168;
const HEADER_CENTER_OFFSET_Y = 22; // 頭像與名字中心
const HEADER_SCALE = 0.74;
const MONEY_CENTER_OFFSET_Y = 66; // 金額與下緣保留間距
const NUMBER_START_X = PANEL_LEFT + 405;

// ---- 籌碼圖片數字 ----
const NUMBER_DIGIT_SCALE = 0.44; // 籌碼數字縮放，避免壓到列框
const NUMBER_DIGIT_ADVANCE = NUMBER_4_FRAME_WIDTH * NUMBER_DIGIT_SCALE - 8;
const NUMBER_COMMA_ADVANCE = NUMBER_4_FRAME_WIDTH * NUMBER_DIGIT_SCALE; // 逗號保留完整字格
const NUMBER_COMMA_LEADING_GAP = 4; // 逗號與前一個數字之間的間距
const NUMBER_SYMBOL_SCALE = NUMBER_DIGIT_SCALE;
const NUMBER_SYMBOL_WIDTH = RESULT_NUMBER_SYMBOL_FRAME_WIDTH * NUMBER_SYMBOL_SCALE;
const NUMBER_MINUS_ADVANCE = NUMBER_SYMBOL_WIDTH - 4;
const NUMBER_GROUP_GAP = 4;
const SYMBOL_FRAME_POSITIVE = 0;
const SYMBOL_FRAME_NEGATIVE = 1;

// ---- 勝章原 APP 循環動畫 ----
const BADGE_FADE_OUT_MS = 200;
const BADGE_FADE_IN_MS = 300;
const BADGE_SETTLE_MS = 300;
const BADGE_HOLD_MS = 2000;
const BADGE_SCALE_MIN = 0.9;
const BADGE_SCALE_MAX = 1.2;

// ---- 底部按鈕 ----
const BUTTON_Y = 646;
const BUTTON_EXIT_X = 480;
const BUTTON_CONTINUE_X = 800;
const BUTTON_WIDTH = 270;
const BUTTON_EXIT_INDEX = 0;
const BUTTON_CONTINUE_INDEX = 1;

// ---- 跨局狀態對話框 ----
const SESSION_DIALOG_X = 640;
const SESSION_DIALOG_Y = 360;
const SESSION_DIALOG_WIDTH = 720;
const SESSION_DIALOG_HEIGHT = 360; // 保留四家籌碼不足訊息的五行空間
const SESSION_DIALOG_TITLE_OFFSET = -120; // 標題相對面板中心 Y
const SESSION_DIALOG_MESSAGE_OFFSET = -5; // 內文相對面板中心 Y
const SESSION_DIALOG_BUTTON_Y = 475;
const SESSION_DIALOG_BUTTON_WIDTH = 240;
const SESSION_DIALOG_BUTTON_HEIGHT = 62;
const SESSION_DIALOG_LEFT_BUTTON_X = 500;
const SESSION_DIALOG_RIGHT_BUTTON_X = 780;

export class CResultMoneyScene extends CBaseResultScene {
  private totalRows: Phaser.GameObjects.Container[] = [];
  private deltaRows: Phaser.GameObjects.Container[] = [];
  private symbolRows: Phaser.GameObjects.Image[] = [];
  private moneyTweens: Phaser.Tweens.Tween[] = [];
  private buttons: Phaser.GameObjects.Container[] = [];
  private keyboardFocusIndex = BUTTON_CONTINUE_INDEX;
  private moneySound: Phaser.Sound.BaseSound | null = null;
  private finished = false;
  private sessionDialogButtons: Array<{
    readonly x: number;
    readonly activate: () => void;
  }> | null = null;
  private sessionDialogFocusIndex = 0;
  private sessionDialogSurfaces: CDialogButtonSurface[] = [];

  constructor() {
    super({ key: SceneKey.ResultMoney });
  }

  init(data?: TResultFlowData): void {
    this.resolveFlow(data);
    this.totalRows = [];
    this.deltaRows = [];
    this.symbolRows = [];
    this.moneyTweens = [];
    this.buttons = [];
    this.keyboardFocusIndex = BUTTON_CONTINUE_INDEX;
    this.moneySound = null;
    this.finished = false;
    this.sessionDialogSurfaces = [];
    this.sessionDialogButtons = null;
    this.sessionDialogFocusIndex = 0;
  }

  create(): void {
    this.createBackdrop();
    this.renderPanel();
    this.renderRows();
    this.renderButtons();
    this.startMoneyRun();
    watchReducedMotion(this, () => {
      if (reducedMotion()) this.finishRun();
    });

    this.input.keyboard?.on('keydown', this.handleKeyboard, this);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.stopMoneySound());
  }

  private renderPanel(): void {
    createPageTitle(this, PANEL_CX, TITLE_Y, '籌碼結算');
    this.renderRowPanels();
  }

  private renderRowPanels(): void {
    const graphics = this.add.graphics();
    for (let seat = 0; seat < 4; seat++) {
      const top = ROW_FIRST_TOP + seat * ROW_STEP + ROW_PANEL_TOP_OFFSET;
      graphics.fillGradientStyle(
        ROW_PANEL_FILL_TOP,
        ROW_PANEL_FILL_TOP,
        ROW_PANEL_FILL_BOTTOM,
        ROW_PANEL_FILL_BOTTOM,
        1
      );
      graphics.fillRoundedRect(
        ROW_PANEL_LEFT,
        top,
        ROW_PANEL_W,
        ROW_PANEL_HEIGHT,
        ROW_PANEL_RADIUS
      );
      graphics.lineStyle(ROW_PANEL_BORDER_WIDTH, ROW_PANEL_BORDER, 1);
      graphics.strokeRoundedRect(
        ROW_PANEL_LEFT,
        top,
        ROW_PANEL_W,
        ROW_PANEL_HEIGHT,
        ROW_PANEL_RADIUS
      );
    }
  }

  private renderRows(): void {
    for (let seat = 0 as TSeat; seat < 4; seat = (seat + 1) as TSeat) {
      const player = this.flow.players[seat];
      const chip = this.flow.chips[seat];
      if (!player || !chip) continue;

      const rowTop = ROW_FIRST_TOP + seat * ROW_STEP;
      this.renderBadge(chip, rowTop + ROW_PANEL_HEIGHT / 2);
      this.renderPlayerHeader(HEADER_X, rowTop + HEADER_CENTER_OFFSET_Y, player, HEADER_SCALE);

      const moneyY = rowTop + MONEY_CENTER_OFFSET_Y;
      this.totalRows[seat] = this.add.container(NUMBER_START_X, moneyY);
      this.deltaRows[seat] = this.add.container(NUMBER_START_X, moneyY);
      this.symbolRows[seat] = this.add
        .image(NUMBER_START_X, moneyY, RESULT_NUMBER_SYMBOL_KEY, this.symbolFrame(chip))
        .setOrigin(0, 0.5)
        .setScale(NUMBER_SYMBOL_SCALE);
      this.renderMoneyState(seat, chip.before, Math.abs(chip.delta));
    }
  }

  /** 原 APP：流局四家顯示「和」；勝章另有淡縮循環，敗章保持靜態。 */
  private renderBadge(chip: TResultChip, centerY: number): void {
    let key: string | null = null;
    if (this.flow.summary.kind === RoundSummaryKind.Draw) key = RESULT_DRAW_KEY;
    else if (chip.outcome === ChipOutcome.Win) key = RESULT_WIN_KEY;
    else if (chip.outcome === ChipOutcome.Lose) key = RESULT_LOSE_KEY;
    if (!key) return;

    const badge = this.add.image(BADGE_CX, centerY, key).setOrigin(0.5).setScale(BADGE_SCALE);
    if (key !== RESULT_WIN_KEY || reducedMotion()) return;

    const animation = this.tweens.chain({
      targets: badge,
      loop: -1,
      tweens: [
        {
          scale: BADGE_SCALE * BADGE_SCALE_MIN,
          alpha: 0,
          duration: BADGE_FADE_OUT_MS,
          ease: 'Sine.easeOut',
        },
        {
          scale: BADGE_SCALE * BADGE_SCALE_MAX,
          alpha: 1,
          duration: BADGE_FADE_IN_MS,
          ease: 'Sine.easeOut',
        },
        {
          scale: BADGE_SCALE,
          duration: BADGE_SETTLE_MS,
          completeDelay: BADGE_HOLD_MS,
          ease: 'Sine.easeOut',
        },
      ],
    });
    watchReducedMotion(this, () => {
      if (!reducedMotion()) return;
      animation.stop();
      badge.setScale(BADGE_SCALE).setAlpha(1);
    });
  }

  private symbolFrame(chip: TResultChip): number {
    return chip.delta >= 0 ? SYMBOL_FRAME_POSITIVE : SYMBOL_FRAME_NEGATIVE;
  }

  /** 同步重畫總額、符號、剩餘輸贏，符號會跟著總額字數移動。 */
  private renderMoneyState(seat: TSeat, total: number, remainingDelta: number): void {
    const totalRow = this.totalRows[seat];
    const deltaRow = this.deltaRows[seat];
    const symbol = this.symbolRows[seat];
    const chip = this.flow.chips[seat];
    if (!totalRow || !deltaRow || !symbol || !chip) return;

    const totalWidth = this.renderAmount(totalRow, total);
    const showDelta = chip.delta !== 0 && remainingDelta > 0;
    symbol
      .setPosition(NUMBER_START_X + totalWidth + NUMBER_GROUP_GAP, totalRow.y)
      .setVisible(showDelta);
    deltaRow
      .setPosition(symbol.x + NUMBER_SYMBOL_WIDTH + NUMBER_GROUP_GAP, totalRow.y)
      .setVisible(showDelta);
    if (showDelta) this.renderAmount(deltaRow, remainingDelta);
    else deltaRow.removeAll(true);
  }

  /** 用 number-4 圖片數字由左至右排版，回傳實際顯示寬度。 */
  private renderAmount(row: Phaser.GameObjects.Container, value: number): number {
    row.removeAll(true);
    const text = formatResultChip(value);
    const layout = buildResultMoneyGlyphLayout(text, {
      digitAdvance: NUMBER_DIGIT_ADVANCE,
      commaAdvance: NUMBER_COMMA_ADVANCE,
      commaLeadingGap: NUMBER_COMMA_LEADING_GAP,
      minusAdvance: NUMBER_MINUS_ADVANCE,
    });
    for (const glyph of layout.glyphs) {
      const character = glyph.character;
      const isComma = character === ',';
      const isMinus = character === '-';
      const key = isMinus ? RESULT_NUMBER_SYMBOL_KEY : NUMBER_4_KEY;
      const frame = isMinus
        ? SYMBOL_FRAME_NEGATIVE
        : isComma
          ? NUMBER_4_FRAME_COMMA
          : Number(character);
      row.add(
        this.add.image(glyph.x, 0, key, frame).setOrigin(0, 0.5).setScale(NUMBER_DIGIT_SCALE)
      );
    }
    return layout.width;
  }

  /** 四家同時起跑，各自依輸贏金額使用 1～5 秒；聲音在延遲結束時開始。 */
  private startMoneyRun(): void {
    if (reducedMotion()) {
      this.finishRun();
      return;
    }
    const activeSeats = ([0, 1, 2, 3] as TSeat[]).filter(
      (seat) => (this.flow.chips[seat]?.delta ?? 0) !== 0
    );
    if (activeSeats.length === 0) {
      this.finished = true;
      return;
    }

    let started = false;
    let completedCount = 0;
    for (const seat of activeSeats) {
      const chip = this.flow.chips[seat];
      if (!chip) continue;
      const tween = this.tweens.addCounter({
        from: 0,
        to: 1,
        delay: MONEY_RUN_DELAY_MS,
        duration: getMoneyRunDurationMs(chip.delta),
        onStart: () => {
          if (started) return;
          started = true;
          this.playMoneySound();
        },
        onUpdate: (runningTween) => {
          const progress = runningTween.getValue() ?? 0;
          this.renderMoneyState(
            seat,
            chip.before + chip.delta * progress,
            Math.abs(chip.delta) * (1 - progress)
          );
        },
        onComplete: () => {
          this.renderMoneyState(seat, chip.after, 0);
          completedCount += 1;
          if (completedCount === activeSeats.length) this.finishRun();
        },
      });
      this.moneyTweens.push(tween);
    }
  }

  /** 立即定數；只由按鈕 pointerdown 或鍵盤確認觸發。 */
  private finishRun(): void {
    if (this.finished) return;
    this.finished = true;
    for (const tween of this.moneyTweens) tween.stop();
    for (let seat = 0 as TSeat; seat < 4; seat = (seat + 1) as TSeat) {
      const chip = this.flow.chips[seat];
      if (chip) this.renderMoneyState(seat, chip.after, 0);
    }
    this.stopMoneySound();
    for (let seat = 0; seat < this.flow.chips.length; seat++) {
      if (this.flow.chips[seat]?.delta)
        glow(
          this,
          ROW_PANEL_LEFT + ROW_PANEL_W / 2,
          ROW_FIRST_TOP + seat * ROW_STEP + ROW_PANEL_TOP_OFFSET + ROW_PANEL_HEIGHT / 2,
          ROW_PANEL_W,
          ROW_PANEL_HEIGHT
        );
    }
  }

  private playMoneySound(): void {
    if (!loadSettings().audioOn || !this.cache.audio.exists(SFX_RESULT_MONEY_KEY)) return;
    try {
      this.moneySound = this.sound.add(SFX_RESULT_MONEY_KEY, { loop: true });
      this.moneySound.play();
    } catch {
      this.moneySound = null;
    }
  }

  private stopMoneySound(): void {
    this.moneySound?.stop();
    this.moneySound?.destroy();
    this.moneySound = null;
  }

  private renderButtons(): void {
    const exitButton = this.createResultButton(
      BUTTON_EXIT_X,
      BUTTON_Y,
      RESULT_BTN_EXIT_KEY,
      () => {
        this.finishRun();
        this.scene.start(SceneKey.Home);
      },
      { width: BUTTON_WIDTH }
    );
    const continueButton = this.createResultButton(
      BUTTON_CONTINUE_X,
      BUTTON_Y,
      RESULT_BTN_CONTINUE_KEY,
      () => {
        this.finishRun();
        this.continueGameSet();
      },
      { width: BUTTON_WIDTH }
    );
    this.buttons = [exitButton, continueButton];

    for (const [index, button] of this.buttons.entries()) {
      button.on('pointerdown', () => this.finishRun());
      button.on('pointerover', () => this.focusButton(index));
      button.on('pointerout', () => this.focusButton(this.keyboardFocusIndex));
    }
    this.focusButton(BUTTON_CONTINUE_INDEX);
  }

  private continueGameSet(): void {
    const save = loadGameSave();
    const active = save.activeSession;
    if (!active) {
      this.scene.start(SceneKey.GameSetup);
      return;
    }
    if (active.status === GameSetStatus.Completed) {
      if (active.completionReason === GameSetCompletionReason.InsufficientBalance) {
        this.showInsufficientBalanceDialog(active, save.ledger.balances);
      } else {
        this.scene.start(SceneKey.SessionSummary);
      }
      return;
    }
    if (active.status === GameSetStatus.CircleComplete) {
      this.showCircleCompleteDialog(active);
      return;
    }
    if (active.status !== GameSetStatus.BetweenRounds) {
      this.scene.start(SceneKey.Dice, { autoStartGame: true });
      return;
    }
    if (!canAllPlayersAfford(save.ledger.balances, active.bet)) {
      saveActiveSession(completeGameSetForInsufficientBalance(active));
      this.scene.start(SceneKey.SessionSummary);
      return;
    }

    this.startPreparedRound(active);
  }

  private startPreparedRound(
    active: IGameSetSession,
    bet: IGameSetSession['bet'] = active.bet
  ): void {
    if (isServerTableEnabled()) {
      // 伺服器擲骰與準備下一手（金幣不夠時伺服器會直接結束這一將）
      nextRound()
        .then(() => {
          const status = loadGameSave().activeSession?.status;
          this.scene.start(
            status === GameSetStatus.Completed ? SceneKey.SessionSummary : SceneKey.Dice,
            { autoStartGame: true }
          );
        })
        .catch((error: unknown) => noticeFromError(error));
      return;
    }
    const id = (prefix: string): string =>
      `${prefix}-${Date.now()}-${Math.floor(Math.random() * 1_000_000_000)}`;
    const next = prepareNextRound(active, {
      roundId: id('round'),
      transactionId: id('settlement'),
      openingDice: rollDice(Math.random),
      wallSeed: Math.floor(Math.random() * 0x1_0000_0000),
      bet,
    });
    saveActiveSession(next);
    traceGameSet('round.plan.saved', next, {}, loadGameSave().ledger.balances);
    this.scene.start(SceneKey.Dice, { autoStartGame: true });
  }

  private showCircleCompleteDialog(active: IGameSetSession): void {
    const nextCircle = active.prevailingWind;
    this.renderSessionDialog(
      '本圈完成',
      `下一圈：${this.windName(nextCircle)}風圈\n目前 ${active.bet.base} 底／${active.bet.taiUnit} 台`,
      [
        {
          x: SESSION_DIALOG_LEFT_BUTTON_X,
          label: '沿用底／台',
          activate: () => this.startPreparedRound(active),
        },
        {
          x: SESSION_DIALOG_RIGHT_BUTTON_X,
          label: '重新選擇',
          activate: () => this.scene.start(SceneKey.GameSetup),
        },
      ]
    );
  }

  private showInsufficientBalanceDialog(
    active: IGameSetSession,
    balances: readonly [number, number, number, number]
  ): void {
    const minimum = active.bet.base + active.bet.taiUnit;
    const insufficientPlayers = active.players
      .filter((player) => balances[player.seat] < minimum)
      .map(
        (player) =>
          `${player.name}：${balances[player.seat].toLocaleString()}（需 ${minimum.toLocaleString()}）`
      )
      .join('\n');
    this.renderSessionDialog('籌碼不足', `${insufficientPlayers}\n目前牌局組已結束`, [
      {
        x: SESSION_DIALOG_X,
        label: '查看總結算',
        activate: () => this.scene.start(SceneKey.SessionSummary),
      },
    ]);
  }

  private renderSessionDialog(
    title: string,
    message: string,
    buttons: ReadonlyArray<{
      readonly x: number;
      readonly label: string;
      readonly activate: () => void;
    }>
  ): void {
    const previous = new Set(this.children.list);
    this.sessionDialogButtons = [...buttons];
    this.sessionDialogFocusIndex = 0;
    this.buttons.forEach((button) => button.disableInteractive());
    this.add
      .rectangle(
        LOGICAL_WIDTH / 2,
        LOGICAL_HEIGHT / 2,
        LOGICAL_WIDTH,
        LOGICAL_HEIGHT,
        0x000000,
        0.72
      )
      .setDepth(100)
      .setInteractive();
    createDialogPanel(
      this,
      SESSION_DIALOG_X,
      SESSION_DIALOG_Y,
      SESSION_DIALOG_WIDTH,
      SESSION_DIALOG_HEIGHT
    ).setDepth(101);
    this.add
      .text(SESSION_DIALOG_X, SESSION_DIALOG_Y + SESSION_DIALOG_TITLE_OFFSET, title, {
        color: '#f4dba1',
        fontFamily: PAGE_THEME.font,
        fontSize: '38px',
        fontStyle: 'bold',
      })
      .setOrigin(0.5)
      .setDepth(102);
    this.add
      .text(SESSION_DIALOG_X, SESSION_DIALOG_Y + SESSION_DIALOG_MESSAGE_OFFSET, message, {
        align: 'center',
        color: PAGE_THEME.text,
        fontFamily: PAGE_THEME.font,
        fontSize: '23px',
      })
      .setOrigin(0.5)
      .setDepth(102);

    this.sessionDialogSurfaces = [];
    for (const [index, button] of buttons.entries()) {
      const surface = createDialogButtonSurface(
        this,
        button.x,
        SESSION_DIALOG_BUTTON_Y,
        SESSION_DIALOG_BUTTON_WIDTH,
        SESSION_DIALOG_BUTTON_HEIGHT
      ).setDepth(102);
      surface.setPrimary(index === 0);
      this.sessionDialogSurfaces.push(surface);
      this.add
        .text(button.x, SESSION_DIALOG_BUTTON_Y, button.label, {
          color: PAGE_THEME.text,
          fontFamily: PAGE_THEME.font,
          fontSize: '26px',
          fontStyle: 'bold',
        })
        .setOrigin(0.5)
        .setDepth(103);
      const hitArea = this.add
        .zone(
          button.x,
          SESSION_DIALOG_BUTTON_Y,
          SESSION_DIALOG_BUTTON_WIDTH,
          SESSION_DIALOG_BUTTON_HEIGHT
        )
        .setDepth(104)
        .setInteractive({ useHandCursor: true })
        .on('pointerover', () => this.focusSessionDialogButton(index))
        .on('pointerdown', () => {
          playUIClick(this);
          button.activate();
        });
      surface.bindInteraction(hitArea);
    }
    const contents = this.children.list.filter((child) => !previous.has(child));
    const root = this.add.container(0, 0, contents).setDepth(100);
    showDialog(this, root, contents[0]!, SESSION_DIALOG_X, SESSION_DIALOG_Y);
    this.focusSessionDialogButton(0);
  }

  private windName(wind: IGameSetSession['prevailingWind']): string {
    switch (wind) {
      case 'east':
        return '東';
      case 'south':
        return '南';
      case 'west':
        return '西';
      case 'north':
        return '北';
    }
  }

  /** 左右切換焦點；Enter／Space／遙控器確認鍵啟動焦點按鈕。 */
  private handleKeyboard(event: KeyboardEvent): void {
    if (this.sessionDialogButtons) {
      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        event.preventDefault();
        const direction = event.key === 'ArrowLeft' ? -1 : 1;
        const count = this.sessionDialogButtons.length;
        this.focusSessionDialogButton((this.sessionDialogFocusIndex + direction + count) % count);
        return;
      }
      if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
        event.preventDefault();
        playUIClick(this);
        this.sessionDialogButtons[this.sessionDialogFocusIndex]?.activate();
      }
      return;
    }
    if (event.key === 'Escape' || event.key === 'Backspace') {
      event.preventDefault();
      this.finishRun();
      playUIClick(this);
      const exitButton = this.buttons[BUTTON_EXIT_INDEX];
      if (exitButton) this.activateResultButton(exitButton);
      return;
    }
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.focusButton(BUTTON_EXIT_INDEX);
      return;
    }
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.focusButton(BUTTON_CONTINUE_INDEX);
      return;
    }
    if (event.key !== 'Enter' && event.key !== ' ') return;

    event.preventDefault();
    this.finishRun();
    playUIClick(this);
    const button = this.buttons[this.keyboardFocusIndex];
    if (button) this.activateResultButton(button);
  }

  private focusButton(index: number): void {
    this.keyboardFocusIndex = index;
    this.buttons.forEach((button, buttonIndex) => {
      this.setResultButtonFocused(button, buttonIndex === index);
    });
  }

  private focusSessionDialogButton(index: number): void {
    const button = this.sessionDialogButtons?.[index];
    if (!button) return;
    this.sessionDialogFocusIndex = index;
    this.sessionDialogSurfaces.forEach((surface, buttonIndex) =>
      surface.setFocused(buttonIndex === index)
    );
  }
}
